<template>
    <b-card class="m-0 h-100" no-body :style="generateVariantStyle(Variant)">
        <!-- En tête du tableau d'utilisateur -->
        <b-card-header :header-bg-variant="Variant" class="text-white py-2" role="tab">
            <b-container class="m-0 p-0" align-h="between" fluid>
                <b-row no-gutters align-v="stretch">
                    <!-- Champ de recherche -->
                    <b-col v-if="ShowSearchField" cols="4" class="d-flex justify-content-start align-items-center">
                        <b-input-group>
                            <template #prepend>
                                <b-button :variant="Variant"
                                          class="rounded-0">
                                    <b-icon font-scale="1.3" :class="`text-${Variant}-light`" icon="search"/>
                                </b-button>
                            </template>
                            <b-form-input type="search" :placeholder="$t('Search')" debounce="500" v-model="search" autocomplete="off"/>
                        </b-input-group>
                    </b-col>
                    <!-- Toolbar de gauche -->
                    <b-col v-if="parameters.IsUsedToolbarLeft" cols="auto">
                        <!-- Permet de configurer des boutons qui sont placé en haut à gauche à la suite du champ de recherche -->
                        <slot name="toolbar-left" :SelectedRows="parameters.SelectedRows">
                        </slot>
                    </b-col>
                    <b-col></b-col>
                    <!-- Toolbar bar de droite -->
                    <b-col md="auto" class="justify-content-end align-items-center">
                        <b-button-toolbar>
                            <b-button-group>
                                <!-- Bouton de sélection de colonnes -->
                                <b-dropdown v-if="ShowColumnsSelector" v-b-tooltip.hover :title="$t('EditColumns')"
                                            :variant="Variant" right
                                            menu-class="pt-1 pr-3"
                                            :toggle-class="`border-0 rounded-pill text-${Variant}-light`"
                                            :disabled="CanDisabledColumns" class="ml-1 h-100">
                                    <template #button-content>
                                        <b-icon icon="layout-three-columns"/>
                                    </template>
                                    <b-dropdown-form class="px-1"> <!-- TODO - à voir -->
                                        <!-- Liste des en-têtes triées disponibles et visibles sous forme d'une coche -->
                                        <b-form-checkbox v-for="header in displayedHeaders.filter(x => !x.HideMenu)
                                                                .sort((a, b) => {
                                                                    if (a.Label < b.Label)
                                                                        return -1;
                                                                    if (a.Label > b.Label)
                                                                        return 1;
                                                                    return 0;
                                                                })" :key="header.Name" plain class="pl-1 mx-1"
                                                         v-model="header.ShowColumn">
                                            <span v-html="header.Label"/>
                                            </b-form-checkbox>
                                    </b-dropdown-form>
                                </b-dropdown>
                                <!-- Bouton pour sélectionner les items de la liste -->
                                <b-button v-b-tooltip.hover :title="$t('SelectAll')" :variant="Variant"
                                          :class="`text-${Variant}-light`" class="btn-circle ml-1 m-0 border-0"
                                          @click="toggleAllRows()"
                                          v-if="ShowToggleSelectionAll && parameters.EnableToggleSelectionAll">
                                    <b-icon icon="check-all"/>
                                </b-button>
                                <!-- Permet de configurer des boutons qui sont placé en haut à droite à la suite du bouton de selection globale "SelectAll" -->
                                <slot name="toolbar-right" :SelectedRows="parameters.SelectedRows">
                                </slot>
                            </b-button-group>
                        </b-button-toolbar>
                    </b-col>
                </b-row>
            </b-container>
        </b-card-header>

        <!-- Corps du tableau -->
        <b-card-body class="p-0 overflow-auto h-100 mh-100 bg-white">
            <b-table-simple hover responsive striped class="border-left border-right border-bottom m-0">
                <b-thead>
                    <b-tr>
                        <!-- (GAUCHE) En tête de la colonne permettant d'agir individuellement sur une ligne (édition, ou autre action individuelle) -->
                        <b-th v-if="parameters.IsUsedMenuLeft"
                              class="px-1 py-3 mw-100 align-middle text-truncate w-auto">
                                <!-- Permet de configurer l'en-tête des boutons d'édition d'une ligne de données -->
                                <slot name="menu-header-left">
                                </slot>
                        </b-th>
                        <!-- En tête de sélection des lignes affichés. Utilise une checkbox comme visuel -->
                        <b-th v-if="parameters.EnableSelectorCheckBox" class="px-1 py-3 mw-100 align-middle">
                            <b-form-checkbox plain class="text-center align-middle"
                                             @change="toggleDisplaySelected()"
                                             :checked="parameters.SelectedDisplayedRows.length >= (parameters.DisplayedRows.length - parameters.CountDisplayedRowsLock)"
                                             :indeterminate="parameters.SelectedDisplayedRows.length > 0 && parameters.SelectedDisplayedRows.length < (parameters.DisplayedRows.length - parameters.CountDisplayedRowsLock)"/>
                        </b-th>
                        <!-- En tête des colonnes -->
                        <b-th v-for="(column, index) in displayedHeaders.filter(x => x.ShowColumn)"
                              v-bind:key="column.Name" @click="sort(column)"
                              :data-headername="column.Name"
                              :class="CssClasses(undefined, column.CssClasses)"
                              class="px-1 py-3 mw-100 align-middle"
                              :style="column.Width != undefined ? 'width:'+ column.Width + 'px;max-width: ' + column.Width + 'px !important;': ''"
                              v-bind="IsSorted(column) ? { 'aria-sort': IsSorted(column) } : {}"
                              :aria-colindex="index + 2">
                            <b-row :class="IsSorted(column) ? 'pr-1 mr-2': ''" class="m-0 p-0">
                                <b-col class="m-0 p-0">
                                    <span v-if="column.ShowLabel" v-html="column.Label"/>
                                </b-col>
                                <b-col class="m-0 p-0 px-1" v-if="column.Tooltip != undefined">
                                    <b-icon icon="info-circle" :variant="Variant"
                                            v-b-tooltip.noninteractive.hover.top :title="column.Tooltip"/>
                                </b-col>
                            </b-row>
                        </b-th>
                        <!-- (DROITE) En tête de la colonne permettant d'agir individuellement sur une ligne (édition, ou autre action individuelle) -->
                        <b-th v-if="parameters.IsUsedMenuRight"
                              class="px-1 py-3 mw-100 align-middle text-center w-auto">
                                <!-- Permet de configurer l'en-tête des boutons d'édition d'une ligne de données -->
                                <slot name="menu-header-right">
                                    {{ $t('Actions') }}
                                </slot>
                        </b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <!-- Lignes du tableau -->
                    <b-tr v-if="!loading && parameters.DisplayedRows.length > 0"
                          v-for="row in parameters.DisplayedRows" v-bind:key="row.Id"
                          @click="toggleRow(row)"
                          @dblclick="$emit('DblClickOnRowsEvent', row)"
                          :class="[CssClasses(row, row.CssClasses), row.Selected == true ? 'bg-light': '']"
                          style="cursor: pointer;">
                        <!-- (GAUCHE) Section d'actions individuels sur la ligne -->
                        <b-td v-if="parameters.IsUsedMenuLeft"
                              class="px-1 text-truncate text-center align-middle"
                              :class="CssClasses(row, row.CssClasses)">
                            <slot name="menu-left" :row="row">
                            </slot>
                        </b-td>
                        <!-- Section de selection -->
                        <b-td v-if="parameters.EnableSelectorCheckBox"
                              class="px-1 py-3 position-relative mw-100 align-content-center justify-content-center">
                            <b-form-checkbox :disabled="row.IsLockedSelect" class="text-center align-middle"
                                             :checked="row.Selected" @click.prevent.stop="toggleRow(row)" plain/>
                        </b-td>
                        <!-- Section de données -->
                        <b-td v-for="column in displayedHeaders.filter(x => x.ShowColumn)" :key="column.Name"
                              class="px-1 align-middle text-center">
                            <slot :name="`column(${column.Name})`" :row="row">
                                <component v-bind:is="'GenericTable' + column.DataType + 'Cell'"
                                           :Value="row[column.Name]"
                                           v-bind="{Header: column}"/>
                            </slot>
                        </b-td>
                        <!-- (DROITE) Section d'actions individuels sur la ligne -->
                        <b-td v-if="parameters.IsUsedMenuRight" class="px-1 text-truncate text-center align-middle">
                            <slot name="menu-right" :row="row">
                            </slot>
                        </b-td>
                    </b-tr>
                    <b-tr v-if="loading || parameters.DisplayedRows.length === 0">
                        <!-- Icône de chargement en lien avec la propriété Loading -->
                        <b-td
                            :colspan="parameters.CountAdditionalColumns + displayedHeaders.filter(a => a.ShowColumn).length">
                            <b-row class="text-md-center m-0" v-if="loading">
                                <b-col class="p-0">
                                    <b-img height="150rem" width="150rem" center
                                           :src="AbsoluteLink('Images/Dcx_Loading.png')"/>
                                    <h6 class="text-primary">{{ $t('Loading') }}</h6>
                                </b-col>
                            </b-row>
                            <template v-else-if="parameters.DisplayedRows.length === 0">
                                {{ $t('NoData') }}
                                {{ parameters.SelectedDisplayedRows.length > 0 ? $t('Found') : '' }}
                            </template>
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-card-body>

        <!-- Pied du tableau -->
        <b-card-footer :header-bg-variant="Variant" class="bg-white py-0 border-top-0 d-flex flex-column" role="tab"
                       v-if="parameters.FilteredRows.length > PerPage && !loading && parameters.DisplayedRows.length > 0">
            <b-row class="w-100 mw-100 py-1">
                <!-- Pagination -->
                <b-col class="w-100">
                    <b-row class="p-0 m-0">
                        <b-col class="p-0 m-0"></b-col>
                        <b-col class="p-0 m-0" md="auto">
                            <b-pagination v-if="parameters.FilteredRows.length > PerPage"
                                          class="py-2 m-0 d-flex justify-content-center"
                                          :total-rows="parameters.FilteredRows.length" v-model="currentPage"
                                          :per-page="PerPage">
                                <template #page="{ page, active }">
                                    {{ page }}
                                </template>
                            </b-pagination>
                        </b-col>
                        <b-col class="p-0 m-0"></b-col>
                    </b-row>
                </b-col>
                <!-- Affiche le compteur d'éléments sélectionnés par l'utilisateur -->
                <b-col class="px-1 align-self-center" md="auto" align-self="center"
                       v-if="ShowSelectedRowNumber && parameters.EnableSelectorCheckBox">
                    ({{ $t('SelectedRows') }}
                    {{ parameters.SelectedRows.length }}
                    {{ $t('On') }}
                    {{ Rows.length }})
                </b-col>
                <!-- Affiche le compteur de page présenté à l'utilisateur -->
                <b-col v-if="ShowCountRowNumber" class="text-right px-1 align-self-center" md="auto">
                    {{
                        (currentPage * PerPage > parameters.FilteredRows.length ? parameters.FilteredRows.length : currentPage * PerPage) + " " + $t('On') + " " + parameters.FilteredRows.length
                    }}
                </b-col>
            </b-row>
        </b-card-footer>
    </b-card>
</template>

<script lang="ts">

/** Traductions */
import locales from "./GenericTable/locales/GenericTable.json";

/** Types vue.js */
import Component, {mixins} from 'vue-class-component';
import {Emit, InjectReactive, Prop, PropSync, Vue, Watch} from 'vue-property-decorator';

/** Types personnalisés et utilitaires */
import {LinkUtils, TableUtils, ThemeUtils} from '@/components/commons/Utils';
import Dictionary from '@/types/Dictionary';

/** Types liés à la GenericTable */
import {IRow} from "./GenericTable/GenericTableRow";
import {IHeader} from "./GenericTable/GenericTableHeader";
import {ICssClass} from "./GenericTable/GenericTableCssClass";
import {SelectionTypeEnum} from "./GenericTable/GenericTableEnums";

/** Import des différents types de colonnes du tableau */
import GenericTableBooleanCell
    from "./GenericTable/GenericTableCells/GenericTableBooleanCell.vue";
import GenericTableStringCell
    from "./GenericTable/GenericTableCells/GenericTableStringCell.vue";
import GenericTableDateCell
    from "./GenericTable/GenericTableCells/GenericTableDateCell.vue";
import GenericTableDecimalCell
    from "./GenericTable/GenericTableCells/GenericTableDecimalCell.vue";
import GenericTableIconCell 
    from "./GenericTable/GenericTableCells/GenericTableIconCell.vue";

/** Import des différents modes du tableau */
import {
    GenericTableModeNoSelect
} from "./GenericTable/GenericTableSelectMode/GenericTableModeNoSelect";
import {
    GenericTableModeSimpleSelect
} from "./GenericTable/GenericTableSelectMode/GenericTableModeSimpleSelect";
import {
    GenericTableModeMultiSelect
} from "./GenericTable/GenericTableSelectMode/GenericTableModeMultiSelect";

import {IGenericTableSelectMode} from "./GenericTable/GenericTableSelectMode";
import {GenericTableParameters} from "./GenericTable/GenericTableParameters";

/** Export des classes composantes de GenericTable */
export * from "./GenericTable/GenericTableHeader";
export * from "./GenericTable/GenericTableRow";
export * from "./GenericTable/GenericTableEnums";
export * from "./GenericTable/GenericTableCssClass";

/**
 * Table générique d'affichage de data set avec fonction selection/pagination/tri/filtre de recherche
 *
 * slots :
 *  toolbar-left                    → permet de configurer des boutons qui sont placés en haut à gauche à la suite du champ de recherche
 *  toolbar-right                   → permet de configurer des boutons qui sont placés en haut à droite à la suite du bouton de selection globale "SelectAll"
 *  menu-header-left                → permet de configurer l'en-tête des boutons d'édition d'une ligne de données
 *  menu-left                       → permet de configurer des boutons d'édition d'une ligne de données
 *  menu-header-right               → permet de configurer l'en-tête des boutons d'édition d'une ligne de données
 *  menu-right                      → permet de configurer des boutons d'édition d'une ligne de données
 *  column({column.name})           → permet de personalisé l'affichage d'une colonne
 *
 * Events :
 *  DblClickOnRowsEvent             → Événement levé lors d'un double click sur ligne du tableau
 *  DeleteRowEvent                  → Suppression de la ligne par le bouton X
 *  ModifyRowEvent                  → Modification de la ligne par le bouton crayon
 */
@Component({
    components: {
        GenericTableBooleanCell,
        GenericTableStringCell,
        GenericTableDecimalCell,
        GenericTableDateCell,
        GenericTableIconCell
    },
    i18n: {messages: locales}
})
export default class GenericTable extends mixins(TableUtils, LinkUtils, ThemeUtils) {
    /** Pour avoir accès à l'énumération "SelectionTypeEnum" dans la partie "template". */
    private selectionTypeEnum = SelectionTypeEnum;

    /** Paramètres du composant */
    private parameters: GenericTableParameters = new GenericTableParameters(this);

    /** Variant du composant */
    @InjectReactive()
    public Variant!: string;

    /** Droits associés à table */
    @InjectReactive()
    public Action!: string;

    /** Descriptif des colonnes du tableau */
    @Prop()
    public Headers!: IHeader[];

    @Prop({default: () => []})
    /** Liste des lignes de données du tableau */
    public Rows!: IRow[];

    /** Affiche le champ de recherche */
    @Prop({default: () => true})
    public ShowSearchField: boolean;

    /** Affiche le sélecteur de colonnes*/
    @Prop({default: () => false})
    public ShowColumnsSelector: boolean;

    /**
     * Affiche le commutateur de selection sur l'ensemble de lignes disponible à la selection
     * @remark : ne s'affiche pas si le generic table ne permet pas la multiselection
     */
    @Prop({default: () => true})
    public ShowToggleSelectionAll: boolean;

    /** Désactive le bouton "Sélectionner tout" */
    @Prop({default: () => SelectionTypeEnum.Multi})
    public SelectionType!: SelectionTypeEnum;

    /** Affichage du nombre d'éléments sélectionnés dans la liste de données */
    @Prop({default: () => false})
    public ShowSelectedRowNumber!: boolean;

    /** Affichage du nombre d'éléments sélectionnés dans la liste de données */
    @Prop({default: () => false})
    public ShowCountRowNumber!: boolean;

    /** Ne pas afficher la liste des colonnes pouvant être affichées */
    @Prop({default: () => false})
    public CanDisabledColumns!: boolean;

    /** Active la pagination intégrée au composant */
    @Prop({default: () => false})
    public EnablePagination!: boolean;

    /** Nombre de lignes par page. */
    @Prop({default: () => 10})
    public PerPage!: number;

    /** Chargement du tableau - Affichage de l'icône de chargement */
    @PropSync('Loading', {default: () => false})
    public loading!: boolean;

    /** Numéro de la page de résultats actuellement visible */
    @Prop({default: () => 1})
    public CurrentPage!: number;

    @Emit('DblClickOnRowsEvent')
    public DblClickOnRowsEvent(row) {
        console.debug('GenenricTable - DblClickOnRowsEvent');
    }

    @Emit('ClickOnRowsEvent')
    public ClickOnRowsEvent(row) {
        console.debug('GenenricTable - ClickOnRowsEvent');
    }

    /** Liste des en têtes de colonnes qui sont affichées */
    private displayedHeaders: IHeader[] = [];

    @Watch('Headers', {deep: true})
    protected loadDisplayHeaders() {
        // Initialize la liste des en-têtes à afficher à partir de la liste complète (Headers) et du masque d'en-têtes
        this.displayedHeaders = [...this.Headers];
        this.loadDisplayRows();
    }

    /** Charge les données interne en prenant les données externes comme référence */
    @Watch('Rows', {deep: true})
    private loadDisplayRows() {
        /** Affichage de l'icône de chargement */
        Vue.set(this, 'loading', true);

        /** Purge les tableaux internes */
        if (this.parameters == undefined)
            this.parameters = new GenericTableParameters(this);
        this.parameters.ResetRows();

        if(this.Rows.length < 1)
            return;

        /** Le tableau interne reprend les données du tableau d'entrée.*/
        let targetRows: IRow[] = [];
        this.Rows.forEach(row => {
            targetRows.push(row);
        });

        /** Nous trions le tableau cible. */
        this.sortColumn(this.headerKeySorted, targetRows, false);

        /** Applique le filtre, si le champ de recherche n'est pas vide */
        if (this.search && this.search !== "")
            targetRows = this.filterRows(this.search.trim(), targetRows);

        /** Sauvegarde le résultat du tri dans les paramètres */
        targetRows.forEach(row => {
            this.parameters.FilteredRows.push(row)
            if (!row.IsLockedSelect && row.Selected)
                this.parameters.SelectedRows.push(row);
        })

        /** Si une pagination est utilisée, la récupération des données doit se limiter à la fenêtre actuellement affichée.*/
        if (this.EnablePagination && this.currentPage > 0 && this.PerPage > 0) {
            const start = (this.currentPage - 1) * this.PerPage;
            const end = (start + this.PerPage) > this.parameters.FilteredRows.length ? this.parameters.FilteredRows.length : (start + this.PerPage);
            targetRows = targetRows.slice(start, end);
            if (targetRows.length == 0 && this.currentPage > 1) {
                this.currentPage = 1;
                const start = 0;
                const end = (start + this.PerPage) > this.parameters.FilteredRows.length ? this.parameters.FilteredRows.length : (start + this.PerPage);
                targetRows = targetRows.slice(start, end);
            }
        }

        /** Une fois le formatage terminé, on insère les valeurs dans les tableaux internes. */
        targetRows.forEach(row => {
            if (row.IsLockedSelect)
                this.parameters.CountDisplayedRowsLock++;
            else if (row.Selected)
                this.parameters.SelectedDisplayedRows.push(row)
            this.parameters.DisplayedRows.push(row)
        });

        /** Suppression de l'icône de chargement */
        Vue.set(this, 'loading', false);
    }

    /**
     * Génère les classes css à ajouter aux lignes
     * @param value Nécessaire pour l'évaluation des "LogicalExpression" (eval()) à ne pas supprimer (cf : Sandrine)
     * @param cssClasses Liste de classe à insérer
     */
    private CssClasses(value, cssClasses: ICssClass[]): string[] {
        let css: string[] = [];
        if (cssClasses != undefined && cssClasses != null && cssClasses.length > 0) {
            cssClasses.forEach(c => {
                if (eval(c.LogicalExpression))
                    css.push(c.CssClass);
            });
        }
        return css;
    }

    //#region SELECTION / MULTI-SELECTION

    /** Stratégie de sélection */
    private selectionStrategy: IGenericTableSelectMode = undefined;

    /** Permute la selection de toutes les lignes de données sans tenir compte de la pagination */
    private toggleAllRows() {
        this.selectionStrategy.ToggleRows();
    }

    /**
     * Switch l'état de selection de toute une liste d'éléments
     * Remarque utiles → passe tout à sélectionner si au moins une ligne n'est pas sélectionnée, sinon retire tout
     */
    private toggleDisplaySelected() {
        this.selectionStrategy.ToggleDisplayedRows();
    }

    /**
     * Sélectionne une liste de ligne
     * @param row Ligne qui doit permuter sa sélection
     */
    private toggleRow(row: IRow) {
        this.$emit('ClickOnRowsEvent', row);
        this.selectionStrategy.ToggleRow(row, true);
    }

    //#endregion SELECTION / MULTI-SELECTION

    //#region FILTER ITEMS
    /** Filtre de recherche */
    private search: string = "";

    @Watch('search')
    /** Indique tout changement sur le filtre de ligne */
    private onSearchChanged(newVal: string, oldVal: string) {
        if (newVal != oldVal) {
            this.currentPage = 1;
            this.loadDisplayRows();
        }
    }

    /**
     * Filtre les lignes
     * @param rows Lignes à filtrer
     * @param search Élément recherché
     */
    private filterRows(search: string, rows: IRow[]): IRow[] {
        const filterRows: Dictionary<IRow> = {};

        rows.forEach(row => {
            this.displayedHeaders.forEach(column => {
                const cellValue = this.getDeepValue(row, column.Name);

                if (!(row.Id in filterRows) && cellValue !== undefined
                    && JSON.stringify(cellValue).toLowerCase().includes(search.toLowerCase())) {
                    filterRows[row.Id] = row;
                }
            })
        })
        return Object.values(filterRows);
    }

    /**
     * Récupère la valeur d'une propriété, même si son chemin est profond (ex: "Parent.Enfant.Propriete")
     */
    private getDeepValue(obj: any, path: string): any {
        if (!path || !obj) return undefined;
        return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined) ? acc[part] : undefined, obj);
    }
    //#endregion FILTER ITEMS

    //#region SORT ITEMS

    /**
     * Tri effictif d'une colonne du tableau
     * @param column Colonne à trier
     * @param rows Lignes à trier
     */
    private sort(column: IHeader) {
        if (column.Sort == true) {
            let sortColumn = column.Name;
            if (column.SortKey != undefined)
                sortColumn = column.SortKey;
            
            /** Switch le tri sur la colonne (utilise un tableau vide pour skip la section tri)*/
            this.sortColumn(sortColumn);
            /** Reconstruit la page proprement en tenant compte de la pagination*/
            this.loadDisplayRows();
        }
    }

    /**
     * Récupère l'état du tri de la colonne pour le 'aria-sort'
     * @param column Colonne visé
     */
    private IsSorted(column: IHeader): string {
        if (column.Sort === undefined || !column.Sort)
            return undefined
        if (this.headerKeySorted &&
            (this.headerKeySorted === column.Name ||
                column.SortKey != undefined && this.headerKeySorted == column.SortKey)) {
            if (this.nextSort === 0)
                return "ascending";
            else
                return "descending";
        }
        return "none";
    }

    //#endregion SORT ITEMS

    //#region PAGINATION

    /** Numéro de la page de résultats actuellement visible */
    private currentPage: number = 1;

    @Watch('CurrentPage')
    private updateCurrentPage() {
        this.currentPage = this.CurrentPage;
    }

    /** Watcher executé sur les changements de page */
    @Watch('currentPage', {immediate: true})
    private onCurrentPageChanged(newVal: number, oldVal: number) {
        if (newVal != oldVal) {
            /** lève une nouvelle fois ce watch si externe au composant */
            if (this.currentPage !== newVal) {
                this.currentPage = newVal;
            } else {
                /** Recharge la page avec les nouvelles informations */
                this.loadDisplayRows();
            }
        }
    }

    //#endregion PAGINATIONS

    /** Post-création de l'instance */
    protected created() {
        this.loading = true;
        this.currentPage = 1;

        switch (this.SelectionType) {
            case SelectionTypeEnum.Multi:
                this.selectionStrategy = new GenericTableModeMultiSelect(this.parameters);
                break;
            case SelectionTypeEnum.Simple:
                this.selectionStrategy = new GenericTableModeSimpleSelect(this.parameters);
                break;
            default:
                this.selectionStrategy = new GenericTableModeNoSelect(this.parameters);
                break;
        }
        this.selectionStrategy.Initialize();

        this.displayedHeaders = [...this.Headers];
        this.loadDisplayRows();
    }
}

</script>

<style scoped>

/** Pour que la page active prenne en compte la couleur de Variant */
:deep(.page-item.active .page-link) {
    background-color: var(--Variant) !important;
}

:deep(.page-link:hover) {
    background-color: var(--VariantLight) !important;
}

/*
:deep(.custom-control-input:is(:checked, :indeterminate) ~ .custom-control-label::before) {
    background-color: var(--Variant) !important;
    border-color: var(--Variant) !important;
}

:deep(.custom-checkbox .custom-control-input:disabled:is(:checked, :indeterminate) ~ .custom-control-label::before) {
    background-color: var(--VariantLight) !important;
    border-color: var(--Variant) !important;
}

:deep(.custom-control-input ~ .custom-control-label::after) {
    outline: 0px solid var(--VariantLight) !important;
}*/

/** Variable --Variant injectée via generateVariantStyle(Variant) dans ObjectDisplaying (composant commun à toute la fiche objet) */
:deep(input[type=checkbox]:is(:checked, :indeterminate)) {
    /** Change la couleur de la checkbox */
    accent-color: var(--Variant) !important;
}

:deep(input[type=radio]:is(:checked, :indeterminate)) {
    /** Change la couleur du bouton radio */
    accent-color: var(--Variant) !important;
}


</style>